import { defineStore } from 'pinia';
import axiosInstance, { useAxios } from '@/axios';
import { useGlobalComponentsStore } from '@/stores/global';
import { i18n, vuetify } from '@/main';
import { NotificationClass } from '@/enums/NotificationClassEnum';
import useUserSession from '@/composables/useUserSession';
import { type userType } from '@/stores/settings-module/usersStore';
import { supportedLanguages, type SupportedLanguage } from '@/languages';
import * as yup from 'yup';
import yupLocale from '@/plugins/yup/locale';

export type UserSession = {
    isAuthenticated: boolean;
    accessToken: string | null;
    accessTokenExpiration: string | null;
    refreshToken: string | null;
    refreshTokenTimeout: ReturnType<typeof setTimeout> | undefined;
    userId: number | null;
    language: string;
    twoFactorVerified: boolean;
};

export type MeType = {
    user?: userType;
};

const { savePartialUserSession, getAccessTokenExpiration, getRefreshTokenTimeout, getRefreshToken, userSession } =
    useUserSession();

export const useLoginStore = defineStore('loginStore', {
    state: (): { me: MeType; fetchDebounce: { [key: string]: Promise<any> } } => {
        return {
            me: {},
            fetchDebounce: {},
        };
    },
    getters: {
        getUser: (state) => state.me.user,
        getUserFullName: (state) => `${state.me.user?.name_first} ${state.me.user?.name_last}`,
        getUserEmail: (state) => `${state.me.user?.email}`,
        getUserPhone: (state) => `${state.me.user?.phone_mobile ?? ''}`,
        getUserInitials: (state) => {
            const firstName = state.me.user?.name_first;
            const lastName = state.me.user?.name_last;
            return `${firstName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`;
        },
        getUserId: (state) => state?.me?.user?.id,
        getMe: (state) => state.me,
    },
    actions: {
        startRefreshTokenTimer(): void {
            const expiration = getAccessTokenExpiration();
            if (expiration === null) {
                return;
            }
            const date: Date = new Date(expiration);
            //refreshnuti minutu pred expiraci
            const timeout: number = date.getTime() - Date.now() - 60 * 1000;
            if (getRefreshTokenTimeout() !== undefined) {
                clearTimeout(getRefreshTokenTimeout());
            }
            savePartialUserSession({
                refreshTokenTimeout: setTimeout(this.refreshCredentials, timeout),
            });
        },
        async refreshCredentials(afterRefresh: boolean = false): Promise<void> {
            if (this.fetchDebounce.refresh !== undefined) {
                // prevent multiple requests
                return this.fetchDebounce.refresh;
            }

            const globalStore = useGlobalComponentsStore();
            if (afterRefresh) {
                globalStore.showLoading = true;
            }

            this.fetchDebounce.refresh = axiosInstance
                .post('auth/refresh', {
                    refresh_token: getRefreshToken(),
                })
                .then((response) => {
                    this.setCredentials(
                        response.data.access_token,
                        response.data.refresh_token,
                        response.data.access_expiration_at,
                        response.data.user_id,
                        response.data.two_factor_verified
                    );
                    if (afterRefresh) {
                        globalStore.showLoading = false;
                    }
                })
                .catch((error) => {
                    if (afterRefresh) {
                        globalStore.showLoading = false;
                    }
                    globalStore.setSnackbarTextAndClass(
                        i18n.global.t(error?.response?.data?.message ?? error?.message),
                        NotificationClass.ERROR
                    );
                    this.logout();
                })
                .finally(() => {
                    delete this.fetchDebounce.refresh;
                });

            await this.fetchDebounce.refresh;
        },
        stopRefreshTokenTimer(): void {
            clearTimeout(getRefreshTokenTimeout());
        },
        async logout(redirect: boolean = true): Promise<void> {
            const globalStore = useGlobalComponentsStore();
            this.stopRefreshTokenTimer();
            globalStore.showLoading = true;
            try {
                await useAxios().post(`auth/logout`);
                globalStore.setSnackbarTextAndClass(
                    i18n.global.t('NotificationMessages.logout'),
                    NotificationClass.SUCCESS
                );
                savePartialUserSession({
                    isAuthenticated: false,
                    twoFactorVerified: false,
                    accessToken: null,
                    refreshToken: null,
                    userId: null,
                    accessTokenExpiration: null,
                    refreshTokenTimeout: undefined,
                    language: import.meta.env.VITE_APP_DEFAULT_LANGUAGE,
                });
                this.setLanguage(import.meta.env.VITE_APP_DEFAULT_LANGUAGE);
            } catch (error: any) {
                globalStore.setSnackbarTextAndClass(
                    i18n.global.t(error?.response?.data?.message ?? error?.message),
                    NotificationClass.ERROR
                );
            }

            if (redirect) {
                window.location.href = window.location.origin;
            }

            globalStore.showLoading = false;
        },
        setCredentials(
            accessToken: string,
            refreshToken: string,
            accessTokenExpiration: string,
            userId: number,
            twoFactorVerified: boolean = false
        ): void {
            savePartialUserSession({
                accessToken,
                refreshToken,
                accessTokenExpiration,
                isAuthenticated: twoFactorVerified,
                userId,
                twoFactorVerified,
            });

            if (twoFactorVerified) {
                this.startRefreshTokenTimer();
            }
        },
        setLanguage(language: string): void {
            const locale = (language && supportedLanguages.includes(language as SupportedLanguage)) ? language : import.meta.env.VITE_APP_DEFAULT_LANGUAGE;
            savePartialUserSession({ language: locale });
            i18n.global.locale.value = locale as SupportedLanguage;
            vuetify.locale.current.value = locale;
            yup.setLocale(yupLocale(i18n.global.t));
        },
        async fetchMe() {
            try {
                if (this.fetchDebounce.me !== undefined) {
                    return this.fetchDebounce.me;
                }

                this.fetchDebounce.me = useAxios().get(`me`);
                const user = (await this.fetchDebounce.me).data;
                this.me = {
                    user,
                };
                
                // Nastavit jazyk z dat uživatele, pokud je k dispozici
                if (user?.language) {
                    this.setLanguage(user.language);
                }
                
                const promise = this.fetchDebounce.me;
                delete this.fetchDebounce.me;
                return promise;
            } catch (e: any) {
                console.error(e);
                /* NOTE NEW: PUVODNE PO PREVZETI Z NEWE
                savePartialUserSession({
                    refreshToken: null,
                    refreshTokenTimeout: undefined,
                    isAuthenticated: false,
                    twoFactorVerified: false,
                });*/
                savePartialUserSession({
                    isAuthenticated: false,
                    twoFactorVerified: false,
                    accessToken: null,
                    refreshToken: null,
                    userId: null,
                    accessTokenExpiration: null,
                    refreshTokenTimeout: undefined,
                });
                window.location.href = window.location.origin;
            }
        },
        async handleOTP(otp: string): Promise<boolean> {
            const globalStore = useGlobalComponentsStore();
            if (otp.length !== 6) {
                globalStore.setSnackbarTextAndClass(
                    i18n.global.t('LoginView.otp-error-length'),
                    NotificationClass.ERROR
                );
                return false;
            }

            globalStore.showLoading = true;
            let result: boolean = false;
            try {
                const response = await useAxios().post(`auth/verify-code`, {
                    two_factor_auth_secret: otp,
                });

                const currentUserSession = userSession();

                if (
                    currentUserSession.accessToken === null ||
                    currentUserSession.refreshToken === null ||
                    currentUserSession.accessTokenExpiration === null ||
                    currentUserSession.userId === null
                ) {
                    return false;
                }

                this.setCredentials(
                    currentUserSession.accessToken,
                    currentUserSession.refreshToken,
                    currentUserSession.accessTokenExpiration,
                    currentUserSession.userId,
                    response.data.two_factor_verified
                );

                result = true;
            } catch (error: any) {
                globalStore.setSnackbarTextAndClass(
                    i18n.global.t(error?.response?.data?.message ?? error?.message),
                    NotificationClass.ERROR
                );
            } finally {
                globalStore.showLoading = false;
            }
            return result;
        },
        async sendOTPAgain(): Promise<boolean> {
            const globalStore = useGlobalComponentsStore();
            globalStore.showLoading = true;
            let result: boolean = false;
            try {
                await useAxios().post(`auth/resend-code`);
                globalStore.setSnackbarTextAndClass(
                    i18n.global.t('LoginView.otp-send-again-success'),
                    NotificationClass.SUCCESS
                );
                result = true;
            } catch (error: any) {
                globalStore.setSnackbarTextAndClass(
                    i18n.global.t(error?.response?.data?.message ?? error?.message),
                    NotificationClass.ERROR
                );
                return false;
            } finally {
                globalStore.showLoading = false;
            }
            return result;
        },
        async handleReset(login: string): Promise<boolean> {
            const globalStore = useGlobalComponentsStore();
            globalStore.showLoading = true;
            let result: boolean = false;
            try {
                await useAxios().post(`auth/password/forgot`, {
                    login: login,
                });
                globalStore.setSnackbarTextAndClass(
                    i18n.global.t('LoginView.reset-success'),
                    NotificationClass.SUCCESS
                );
                result = true;
            } catch (error: any) {
                globalStore.setSnackbarTextAndClass(
                    i18n.global.t(error?.response?.data?.message ?? error?.message),
                    NotificationClass.ERROR
                );
            } finally {
                globalStore.showLoading = false;
            }
            return result;
        },
        async handleLogin(login: string, password: string): Promise<number> {
            const globalStore = useGlobalComponentsStore();
            globalStore.showLoading = true;
            let result: number = 0;
            try {
                const response = await axiosInstance.post(`auth/login`, {
                    login: login,
                    password: password,
                });

                this.setCredentials(
                    response.data.access_token,
                    response.data.refresh_token,
                    response.data.access_expiration_at,
                    response.data.user_id,
                    response.data.two_factor_verified
                );
                result = response.data.two_factor_verified ? 1 : 2;
            } catch (error: any) {
                globalStore.setSnackbarTextAndClass(
                    i18n.global.t(error?.response?.data?.message ?? error?.message),
                    NotificationClass.ERROR
                );
            } finally {
                globalStore.showLoading = false;
            }
            return result;
        },
    },
});
